import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import careerService from '../api/careerService';
import Seo from '../components/common/Seo';

import bgImage from '../assets/images/join_our_team.webp';

const JobApplicationPage = () => {
    const { jobId } = useParams();
    const navigate = useNavigate();

    const [jobData, setJobData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        mobile: '',
        address: '',
        description: '',
        resume: null
    });

    // Fetch job data from API
    useEffect(() => {
        const fetchJobData = async () => {
            try {
                const response = await careerService.getAllCareers();
                const careers = response.data?.careers || response.data?.data || response.data || [];
                const careersArray = Array.isArray(careers) ? careers : [];
                const job = careersArray.find(j => (j._id || j.id) === jobId);
                if (job) {
                    setJobData(job);
                } else {
                    setError('Job not found');
                }
            } catch (err) {
                setError('Failed to load job details. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchJobData();
    }, [jobId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileChange = (e) => {
        setFormData(prev => ({
            ...prev,
            resume: e.target.files[0]
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);

        try {
            await careerService.applyForJob({
                fullName: formData.name,
                email: formData.email,
                phone_no: formData.mobile,
                address: formData.address,
                description: formData.description,
                resume: formData.resume
            });
            navigate('/career/application-success');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to submit application. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="w-full h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
            </div>
        );
    }

    if (!jobData) {
        return (
            <div className="w-full h-screen flex flex-col items-center justify-center gap-4">
                <p className="text-gray-600 text-lg">{error || 'Job not found'}</p>
                <button
                    onClick={() => navigate('/career')}
                    className="text-green-600 hover:text-green-800 underline"
                >
                    Back to Careers
                </button>
            </div>
        );
    }

    return (
        <div className="w-full min-h-screen bg-[#F9F9FB] py-10 px-4 md:px-8 lg:px-16">
            <Seo
                title={`${jobData?.title ? `${jobData.title} Application` : 'Job Application'} | Agastya Agro`}
                description={`Submit your application for ${jobData?.title || 'this role'} at Agastya Agro Limited and take the next step in your agriculture career.`}
                noIndex
            />
            <div className="max-w-[1280px] mx-auto">
                {/* Back button */}
                <button
                    onClick={() => navigate('/career')}
                    className="flex items-center gap-2 mb-6 text-gray-600 hover:text-black transition-colors"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
                    </svg>
                    Back to Careers
                </button>

                <div className="bg-white rounded-[24px] shadow-lg overflow-hidden">
                    {/* Background image header */}
                    <div className="w-full h-[200px] md:h-[300px] relative">
                        <img
                            src={bgImage}
                            alt="Agastya Agro careers banner with team imagery"
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                            <h1 className="text-white text-3xl md:text-4xl lg:text-5xl font-[LTWave] font-medium">
                                Join Our Team
                            </h1>
                        </div>
                    </div>

                    {/* Application form */}
                    <div className="p-6 md:p-10">
                        <div className="mb-8">
                            <h2 className="font-inter font-medium text-2xl md:text-3xl leading-[150%] mb-2">
                                #{jobData.title}
                            </h2>
                            <p className="font-inter font-normal text-base md:text-lg text-gray-700">
                                Location: {jobData.location} | {jobData.fullTime ? 'Full-time' : 'Part-time'}
                            </p>
                            <p className="font-inter font-normal text-base md:text-lg mt-4 text-gray-600">
                                {jobData.description}
                            </p>
                        </div>

                        {error && (
                            <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="flex flex-col gap-6 md:gap-8 max-w-3xl">
                            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                                <div className="flex-1">
                                    <label htmlFor="name" className="font-inter font-normal text-[16px] leading-[100%] block mb-2">
                                        Your Name
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="Name"
                                        className="w-full h-[40px] rounded-[4px] border border-gray-300 px-4 py-2 font-inter text-[14px]"
                                        required
                                    />
                                </div>

                                <div className="flex-1">
                                    <label htmlFor="email" className="font-inter font-normal text-[16px] leading-[100%] block mb-2">
                                        Your Email
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="Email"
                                        className="w-full h-[40px] rounded-[4px] border border-gray-300 px-4 py-2 font-inter text-[14px]"
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="mobile" className="font-inter font-normal text-[16px] leading-[100%] block mb-2">
                                    Your Mobile Number
                                </label>
                                <input
                                    type="tel"
                                    id="mobile"
                                    name="mobile"
                                    value={formData.mobile}
                                    onChange={handleChange}
                                    placeholder="Mobile Number"
                                    className="w-full h-[40px] rounded-[4px] border border-gray-300 px-4 py-2 font-inter text-[14px]"
                                    required
                                />
                            </div>

                            <div>
                                <label htmlFor="address" className="font-inter font-normal text-[16px] leading-[100%] block mb-2">
                                    Your Address
                                </label>
                                <input
                                    type="text"
                                    id="address"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleChange}
                                    placeholder="Address"
                                    className="w-full h-[40px] rounded-[4px] border border-gray-300 px-4 py-2 font-inter text-[14px]"
                                    required
                                />
                            </div>

                            <div>
                                <label htmlFor="description" className="font-inter font-normal text-[16px] leading-[100%] block mb-2">
                                    Description
                                </label>
                                <textarea
                                    id="description"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    placeholder="Why do you want to join Agastya"
                                    className="w-full min-h-[100px] rounded-[4px] border border-gray-300 px-4 py-2 font-inter text-[14px]"
                                    required
                                />
                            </div>

                            <div>
                                <label htmlFor="resume" className="font-inter font-normal text-[16px] leading-[100%] block mb-2">
                                    Resume
                                </label>
                                <div className="w-full min-h-[80px] rounded-[4px] border border-gray-300 px-4 py-2 font-inter text-[14px]">
                                    <input
                                        type="file"
                                        id="resume"
                                        name="resume"
                                        onChange={handleFileChange}
                                        className="hidden"
                                        accept=".pdf,.doc,.docx"
                                        required
                                    />
                                    <label htmlFor="resume" className="flex items-center gap-2 cursor-pointer">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="19" viewBox="0 0 18 19" fill="none">
                                            <g opacity="0.2">
                                                <path d="M15.6604 8.80711L8.94815 15.5194C8.12584 16.3417 7.01056 16.8036 5.84765 16.8036C4.68474 16.8036 3.56946 16.3417 2.74716 15.5194C1.92486 14.6971 1.46289 13.5818 1.46289 12.4189C1.46289 11.256 1.92486 10.1407 2.74716 9.31838L9.00658 3.05896C9.55478 2.50979 10.2987 2.20088 11.0746 2.2002C11.8506 2.19951 12.5951 2.5071 13.1442 3.0553C13.6934 3.60351 14.0023 4.34741 14.003 5.12337C14.0037 5.89933 13.6961 6.64378 13.1479 7.19295L6.87385 13.4524C6.59975 13.7265 6.22799 13.8805 5.84035 13.8805C5.45271 13.8805 5.08095 13.7265 4.80685 13.4524C4.53275 13.1783 4.37876 12.8065 4.37876 12.4189C4.37876 12.0312 4.53275 11.6595 4.80685 11.3854L11.0078 5.19169" stroke="#1C1C1C" strokeWidth="1.46077" strokeLinecap="round" strokeLinejoin="round"/>
                                            </g>
                                        </svg>
                                        <span className="font-inter text-[16px] text-[#34343363]">
                                            {formData.resume ? formData.resume.name : 'Choose File or Drop a file Here'}
                                        </span>
                                    </label>
                                </div>
                            </div>

                            <div className="flex justify-start mt-4">
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="bg-black text-white font-inter font-medium px-8 py-3 rounded-[21.26px] min-w-[150px] transition-all duration-300 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {submitting ? 'Submitting...' : 'Apply Now'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default JobApplicationPage;
